import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Alert, Button, DataTable, Pagination } from "../../../ui";
import type { DataColumn } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import { businessCodeLabel, date as formatDateTime } from "../../workspace/model";
import { PageHeader, Panel, RowAction, RowLink, statusBadge } from "../../workspace/shared";
import { memberSearchFormSchema } from "../schema";
import type { MemberSearchFormValues } from "../schema";
import type { Schemas } from "../../workspace/api.generated";

export interface MemberListViewProps {
  readonly filters: MemberSearchFormValues;
  readonly channels?: readonly { value: string; label: string }[];
  readonly plans?: readonly { value: string; label: string }[];
  readonly onExport?: () => void;
  readonly exporting?: boolean;
  readonly exportAllowed?: boolean;
  readonly exportError?: string;
  readonly result?: Schemas["MemberSearchPage"];
  readonly state?: "ready" | "loading" | "error";
  readonly refreshing?: boolean;
  readonly notice?: string;
  readonly detailHref?: (memberId: number) => string;
  readonly onSearch: (filters: MemberSearchFormValues) => void;
  readonly onPageChange: (page: number) => void;
  readonly onRetry: () => void;
  readonly onViewMember: (memberId: number) => void;
}

export function MemberListView({
  filters,
  channels = [], plans = [], onExport, exporting = false, exportAllowed = false, exportError,
  result,
  state = "ready",
  refreshing = false,
  notice,
  detailHref,
  onSearch,
  onPageChange,
  onRetry,
  onViewMember,
}: MemberListViewProps) {
  const form = useForm<MemberSearchFormValues>({
    resolver: zodResolver(memberSearchFormSchema),
    values: filters,
    shouldFocusError: true,
  });
  const columns: readonly DataColumn<Schemas["MemberSearchView"]>[] = [
    { key: "joined", title: "가입일시", render: row => formatDateTime(row.joinedAt) },
    { key: "closed", title: "해지일시", render: row => row.closedAt ? formatDateTime(row.closedAt) : "—" },
    { key: "channel", title: "가입채널", render: row => row.channelName ?? businessCodeLabel(row.channelCode) },
    { key: "plan", title: "요금제", render: row => row.planName ?? "정보 없음" },
    { key: "status", title: "상태", render: row => statusBadge(row.status, row.status === "CLOSED" ? "해지" : undefined) },
    { key: "carrier", title: "통신사", render: row => businessCodeLabel(row.telecomCode) },
    { key: "phone", title: "휴대폰번호", render: row => row.phoneNumber ?? row.phoneNumberMasked },
    { key: "name", title: "이름", render: row => detailHref
      ? <RowLink href={detailHref(row.id)} onNavigate={() => onViewMember(row.id)} triggerKey={`member:${row.id}:detail`}>{row.name ?? row.nameMasked ?? "이름 없음"}</RowLink>
      : <RowAction onClick={() => onViewMember(row.id)}>{row.name ?? row.nameMasked ?? "이름 없음"}</RowAction> },
    { key: "birth", title: "생년월일", render: row => row.birthDate ?? "정보 없음" },
    { key: "gender", title: "성별", render: row => row.genderCode === "MALE" ? "남" : row.genderCode === "FEMALE" ? "여" : "정보 없음" },
  ];

  return (
    <>
      <PageHeader
        id="ADM-S05"
        title="회원 관리"
        hideBody
        description=""
        meta={refreshing ? <p aria-live="polite">최신 목록 확인 중</p> : undefined}
      />
      {notice ? <Alert persistent variant="information" title="회원 목록 복귀">{notice}</Alert> : null}
      {exportError ? <Alert persistent variant="danger" title="다운로드 실패">{exportError}</Alert> : null}
      <Panel title="검색 조건">
        <form
          className="admin-filter-bar"
          noValidate
          onSubmit={form.handleSubmit((values) => onSearch({
            ...values, keyword: values.keyword.trim(),
          }))}
        >
          <HookFormField compact formControl={form.control} label="가입 시작일" name="from" type="date" />
          <HookFormField compact formControl={form.control} label="가입 종료일" name="to" type="date" />
          <HookFormField compact control="select" formControl={form.control} label="가입채널" name="channelCode"><option value="">전체</option>{channels.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</HookFormField>
          <HookFormField compact control="select" formControl={form.control} label="회원 상태" name="status">
            <option value="">전체</option>
            <option value="BASIC">활성화 전</option>
            <option value="ACTIVE">이용 중</option>
            <option value="LOCKED">잠김</option>
            <option value="CLOSED">해지</option>
          </HookFormField>
          <HookFormField compact control="select" formControl={form.control} label="요금제" name="planCode"><option value="">전체</option>{plans.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</HookFormField>
          <HookFormField
            compact
            formControl={form.control}
            label="검색어"
            maxLength={100}
            name="keyword"
            placeholder="회원번호, 이름, 이메일 또는 휴대폰 번호"
          />
          <Button size="small" type="submit">조회</Button>
        </form>
      </Panel>
      {onExport ? <div className="admin-list-actions"><Button size="small" variant="secondary" disabled={!exportAllowed || exporting} isLoading={exporting} onClick={onExport}>다운로드</Button></div> : null}
      <DataTable
        caption="회원 검색 결과"
        columns={columns}
        rows={state === "ready" ? result?.content ?? [] : []}
        rowKey={(row) => row.id}
        state={state}
        onRetry={onRetry}
      />
      {result && state === "ready" ? (
        <Pagination
          page={result.page}
          size={result.size}
          totalCount={result.totalElements}
          onPageChange={onPageChange}
        />
      ) : null}
    </>
  );
}
